import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryNames = {
  amazon: 'أمازون',
  noon: 'نون',
  namshi: 'نمشي',
  aliexpress: 'علي إكسبريس'
};

const categoryDescriptions = {
  amazon: 'اكتشف أفضل عروض أمازون وخصوماته الحصرية. منتجات متنوعة بأسعار لا تُقاوم.',
  noon: 'تصفح عروض نون المميزة وأكواد الخصم الحصرية. تسوق بأفضل الأسعار.',
  namshi: 'أحدث عروض نمشي للأزياء والموضة. ماركات عالمية بخصومات رائعة.',
  aliexpress: 'عروض علي إكسبريس المذهلة. منتجات عالمية بأسعار منافسة وشحن مباشر.'
};

export default function CategoryPage() {
  const { categoryName } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [banner, setBanner] = useState(null);
  const [homeBanner, setHomeBanner] = useState(null);
  const LIMIT = 16;

  const displayName = categoryNames[categoryName] || categoryName;
  const description = categoryDescriptions[categoryName] || `تصفح أفضل العروض المتاحة على ${displayName}`;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    fetchProducts(categoryName, debouncedSearchTerm);
  }, [categoryName, debouncedSearchTerm]);

  useEffect(() => {
    fetchBanner();
    fetchHomeBanner();
  }, [categoryName]);

  const fetchBanner = async () => {
    if (!categoryNames[categoryName]) {
      setBanner(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/settings/banner`, { params: { key: categoryName } });
      setBanner(response.data);
    } catch (error) {
      console.error('خطأ في تحميل البنر:', error);
      setBanner(null);
    }
  };

  const fetchHomeBanner = async () => {
    try {
      const response = await axios.get(`${API}/settings/banner`, { params: { key: 'home' } });
      setHomeBanner(response.data);
    } catch (error) {
      console.error('خطأ في تحميل بنر الصفحة الرئيسية:', error);
      setHomeBanner(null);
    }
  };

  const resolveBannerUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL || ''}${url}`;
  };

  const fetchProducts = async (category = categoryName, term = '') => {
    setLoading(true);
    setSkip(0);
    try {
      const params = new URLSearchParams();
      params.set('category', category);
      params.set('limit', LIMIT);
      params.set('skip', 0);
      if (term) {
        params.set('q', term);
      }

      const countParams = new URLSearchParams();
      countParams.set('category', category);
      if (term) {
        countParams.set('q', term);
      }

      const [productsRes, countRes] = await Promise.all([
        axios.get(`${API}/products?${params.toString()}`),
        axios.get(`${API}/products/count/total?${countParams.toString()}`)
      ]);
      
      setProducts(productsRes.data);
      setHasMore(productsRes.data.length < countRes.data.count);
      setSkip(LIMIT);
    } catch (error) {
      console.error('خطأ في تحميل المنتجات:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      params.set('category', categoryName);
      params.set('limit', LIMIT);
      params.set('skip', skip);
      if (debouncedSearchTerm) {
        params.set('q', debouncedSearchTerm);
      }

      const countParams = new URLSearchParams();
      countParams.set('category', categoryName);
      if (debouncedSearchTerm) {
        countParams.set('q', debouncedSearchTerm);
      }

      const [productsRes, countRes] = await Promise.all([
        axios.get(`${API}/products?${params.toString()}`),
        axios.get(`${API}/products/count/total?${countParams.toString()}`)
      ]);
      
      setProducts(prev => {
        const updated = [...prev, ...productsRes.data];
        setHasMore(updated.length < countRes.data.count);
        return updated;
      });
      setSkip(prev => prev + LIMIT);
    } catch (error) {
      console.error('خطأ في تحميل المزيد:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>عروض {displayName} - صفقة | أفضل الخصومات والعروض</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={`عروض ${displayName}, خصومات ${displayName}, كوبونات ${displayName}, تسوق أونلاين, صفقات`} />
        <meta property="og:title" content={`عروض ${displayName} - صفقة`} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`عروض ${displayName} - صفقة`} />
        <meta name="twitter:description" content={description} />
        <link rel="canonical" href={`${window.location.origin}/category/${categoryName}`} />
      </Helmet>

      <Header searchValue={searchTerm} onSearchChange={setSearchTerm} />
      
      <main className="flex-grow">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-12 md:py-16">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
              منتجات {displayName}
            </h1>
            <p className="text-lg text-muted-foreground">
              تصفح أفضل العروض المتاحة على {displayName}
            </p>
          </div>

          {(() => {
            const platformBannerActive = banner?.image_url && banner?.is_active !== false;
            const fallbackBannerActive = homeBanner?.image_url && homeBanner?.is_active !== false;
            const bannerToShow = platformBannerActive ? banner : fallbackBannerActive ? homeBanner : null;
            if (!bannerToShow) return null;
            return (
            <div className="mb-12">
              <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
                {bannerToShow?.link_url ? (
                  <a href={bannerToShow.link_url} className="block" rel="noopener noreferrer" target="_blank">
                    <img
                      src={resolveBannerUrl(bannerToShow.image_url)}
                      alt={`بنر ${displayName}`}
                      className="w-full h-auto object-cover"
                    />
                  </a>
                ) : (
                  <img
                    src={resolveBannerUrl(bannerToShow.image_url)}
                    alt={`بنر ${displayName}`}
                    className="w-full h-auto object-cover"
                  />
                )}
              </div>
            </div>
            );
          })()}

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-muted-foreground">لا توجد منتجات في هذا التصنيف حالياً</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {hasMore && (
                <div className="flex justify-center mt-12">
                  <Button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="btn-primary px-8 py-6 text-lg"
                    data-testid="load-more-button"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="ml-2 h-5 w-5 animate-spin" />
                        جارٍ التحميل...
                      </>
                    ) : (
                      'تحميل المزيد'
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
